'use client';

import { createClient } from '@/lib/supabase/client';

export interface EEGMetricRow {
  id: string;
  recordedAt: string;
  cognitiveLoad: number;
  attentionScore: number;
  signalQuality: number | null;
}

function isSchemaError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string };
  if (err.code && typeof err.code === 'string') {
    const cls = err.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (err.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return patterns.some((p) => p.test(err.message!));
  }
  return false;
}

export const eegMetricsService = {
  async insert(params: {
    cognitiveLoad: number;
    attentionScore: number;
    signalQuality?: number | null;
    recordedAt?: string;
  }): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('eeg_metrics').insert({
        recorded_at: params.recordedAt ?? new Date().toISOString(),
        cognitive_load: params.cognitiveLoad,
        attention_score: params.attentionScore,
        signal_quality: params.signalQuality ?? null,
      });
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error saving EEG metric:', error.message);
          throw error;
        } else {
          console.warn('EEG metric insert error:', error.message);
        }
      }
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('EEG metric insert failed:', err);
    }
  },

  async fetchRecent(limit = 60): Promise<EEGMetricRow[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('eeg_metrics')
        .select('id, recorded_at, cognitive_load, attention_score, signal_quality')
        .order('recorded_at', { ascending: false })
        .limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching EEG metrics:', error.message);
          throw error;
        } else {
          console.warn('EEG metrics fetch error:', error.message);
          return [];
        }
      }

      return (data ?? [])
        .reverse()
        .map((row) => ({
          id: row.id,
          recordedAt: row.recorded_at,
          cognitiveLoad: row.cognitive_load,
          attentionScore: row.attention_score,
          signalQuality: row.signal_quality ?? null,
        }));
    } catch (err: unknown) {
      if (isSchemaError(err)) throw err;
      console.warn('EEG metrics fetch failed:', err);
      return [];
    }
  },
};
